import type { BackofficeExportFormat } from '../exportArtifacts/exportArtifacts';
import type { UserAccountFilterItem } from './userAccountsGateway';

const DATE_VALUE_REGEXP = /^\d{4}-\d{2}-\d{2}$/;

export type UserAccountQuerySortDir = 'asc' | 'desc' | null | undefined;

export type UserAccountListQueryIntent = {
  filters: UserAccountFilterItem[];
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortDir?: UserAccountQuerySortDir;
};

export type UserAccountExportQueryIntent = Omit<UserAccountListQueryIntent, 'page' | 'pageSize'> & {
  format: BackofficeExportFormat;
};

type CanonicalFilter = {
  field: string;
  op: string;
  value: string;
};

const stableStringify = (value: unknown): string => JSON.stringify(value);

const normalizeDateValue = (value: unknown): string => {
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  const stringValue = String(value ?? '');
  if (DATE_VALUE_REGEXP.test(stringValue)) {
    return stringValue;
  }

  return stringValue;
};

const normalizeValue = (value: unknown): string | number | boolean | null => {
  if (value instanceof Date) {
    return normalizeDateValue(value);
  }

  if (Array.isArray(value)) {
    return JSON.stringify([...value].map((item) => normalizeValue(item)).sort());
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  ) {
    return value;
  }

  return String(value);
};

const normalizeFilterField = (field: string): string => {
  if (field === 'creationTime') return 'createdAt';
  if (field === 'lastSignInTime') return 'lastSignedInAt';
  return field;
};

const normalizeFilterOperator = (field: string, operator: string): string => {
  if (operator === '==' || operator === 'is') return 'equals';
  if (operator === 'array-contains') return 'contains';
  if (
    operator === 'onDate' &&
    (normalizeFilterField(field) === 'createdAt' ||
      normalizeFilterField(field) === 'lastSignedInAt')
  ) {
    return 'equals';
  }
  return operator;
};

const normalizeFilterValue = (value: UserAccountFilterItem['value']) => {
  if (
    Array.isArray(value) &&
    value.every((item) => Object.prototype.toString.call(item) === '[object Date]')
  ) {
    return value.map((item) => normalizeDateValue(item));
  }

  if (value instanceof Date) {
    return normalizeDateValue(value);
  }

  return value;
};

const canonicalizeFilterSortKey = (filter: UserAccountFilterItem): string =>
  `${normalizeFilterField(filter.field)}:${normalizeFilterOperator(filter.field, filter.op)}:${
    Array.isArray(filter.value)
      ? stableStringify([...filter.value].map((value) => normalizeValue(value)).sort())
      : stableStringify(normalizeValue(filter.value))
  }`;

export const isEmptyUserAccountFilterValue = (value: UserAccountFilterItem['value']): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

export const normalizeUserAccountFilter = (
  filter: UserAccountFilterItem
): UserAccountFilterItem | null => {
  const value = normalizeFilterValue(filter.value);
  if (isEmptyUserAccountFilterValue(value)) {
    return null;
  }

  return {
    field: normalizeFilterField(filter.field),
    op: normalizeFilterOperator(filter.field, filter.op),
    value,
  };
};

export const normalizeUserAccountFilters = (
  filters: ReadonlyArray<UserAccountFilterItem>
): UserAccountFilterItem[] =>
  filters
    .map(normalizeUserAccountFilter)
    .filter((filter): filter is UserAccountFilterItem => filter !== null);

export const normalizeUserAccountSortField = (
  sortField: string | null | undefined
): string | undefined => {
  if (sortField === undefined || sortField === null || sortField === '') {
    return undefined;
  }

  return normalizeFilterField(sortField);
};

export const canonicalizeUserAccountFilters = (
  filters: ReadonlyArray<UserAccountFilterItem>
): CanonicalFilter[] =>
  normalizeUserAccountFilters(filters)
    .map<CanonicalFilter>((filter) => ({
      field: filter.field,
      op: filter.op,
      value: Array.isArray(filter.value)
        ? stableStringify([...filter.value].map((value) => normalizeValue(value)).sort())
        : stableStringify(normalizeValue(filter.value)),
    }))
    .sort((left, right) =>
      `${left.field}:${left.op}:${left.value}`.localeCompare(
        `${right.field}:${right.op}:${right.value}`
      )
    );

export const buildCanonicalUserAccountListQueryIntent = (
  params: UserAccountListQueryIntent
): UserAccountListQueryIntent => ({
  filters: [...normalizeUserAccountFilters(params.filters)].sort((left, right) =>
    canonicalizeFilterSortKey(left).localeCompare(canonicalizeFilterSortKey(right))
  ),
  page: params.page,
  pageSize: params.pageSize,
  sortField: normalizeUserAccountSortField(params.sortField),
  sortDir: params.sortDir ?? undefined,
});

export const buildCanonicalUserAccountExportQueryIntent = (
  params: UserAccountExportQueryIntent
): UserAccountExportQueryIntent => ({
  filters: [...normalizeUserAccountFilters(params.filters)].sort((left, right) =>
    canonicalizeFilterSortKey(left).localeCompare(canonicalizeFilterSortKey(right))
  ),
  sortField: normalizeUserAccountSortField(params.sortField),
  sortDir: params.sortDir ?? undefined,
  format: params.format,
});

export const buildCanonicalUserAccountQueryIdentity = (params: {
  filters: ReadonlyArray<UserAccountFilterItem>;
  page?: number;
  pageSize?: number;
  sortField?: string | null;
  sortDir?: UserAccountQuerySortDir;
}) => ({
  filters: canonicalizeUserAccountFilters(params.filters),
  page: params.page,
  pageSize: params.pageSize,
  sortField: normalizeUserAccountSortField(params.sortField) ?? null,
  sortDir: params.sortDir ?? null,
});
